import { useApolloClient } from '@apollo/client'
import { CHAIN_ID_TO_NETWORK, getChainInfo, SupportedNetwork } from '@spritz-finance/sdk'
import {
    Body,
    breakpoints,
    Button,
    colors,
    Icon,
    Margin,
    OpenNewSvg,
    Spacer,
} from '@spritz-finance/ui'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import styled from 'styled-components'
import PayOthersImage from '../../../../assets/icons/pay-someone.png'
import SpritzCardImage from '../../../../assets/icons/spritzcard.svg'
import { ExpandDetailIcon } from '../../../../components/ExpandedDetailIcon'
import { FadePresence } from '../../../../components/FadePresence'
import { Card } from '../../../../components/Layout/Card'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import {
    FullBillFragment,
    FullPaymentFragment,
    PayableAccountType,
    PaymentStatus,
} from '../../../../graphql/generated/graphql'
import { FULL_BILL_FRAGMENT } from '../../../../graphql/schema/liabilities'
import { cssVar } from '../../../../theme/utils'
import { formatCurrency } from '../../../../utils/format'
import { NetworkLogo } from '../../../../web3/networkSwitcher/NetworkLogo'
import { InstitutionImage } from '../InstitutionImage'
import { StatusBadge } from './StatusBadge'

const getLatestTransaction = (payment: FullPaymentFragment) => {
    return (
        payment?.directPayment?.transactionReferences[
            payment?.directPayment!.transactionReferences.length - 1
        ] ?? null
    )
}

const DataWrapper = styled.div`
    border: 1px solid #d6d6d6;
    border-radius: 10px;
    padding: ${Margin.Small};
`

const StyledCard = styled(Card)`
    padding: ${Margin.Large} ${Margin.Med};

    ${breakpoints.xsmall} {
        padding: ${Margin.Med};
        padding-top: ${Margin.Small};
    }
`

const EmbeddedWrapper = styled.div`
    width: 100%;
`

const InfoWrapper = styled.div`
    padding: 16px;
    border-radius: 10px;
    background: var(--surface-3, ${colors.seaSpray[50]});
`

const ExpandedAmountsWrapper = styled.div`
    margin-top: 12px;
    margin-bottom: 12px;
    border-top: 1px solid #d6d6d6;
    border-bottom: 1px solid #d6d6d6;
    padding: 10px 16px;
`

interface DirectPaymentCardProps {
    payment: FullPaymentFragment
    embedded?: boolean
}

const TitleWrapper = styled(Column)`
    & > * {
        display: block;
        height: 24px;
        line-height: 24px;
    }
`

export const DirectPaymentCard: React.VFC<DirectPaymentCardProps> = ({ payment, embedded }) => {
    const client = useApolloClient()
    const [expandedDetail, setExpandedDetail] = useState(false)

    const isBankTransfer = payment.account?.type === PayableAccountType.BankAccount
    const isPayOthers = !payment.account?.ownedByUser
    const isCard = payment.account?.type === PayableAccountType.SpritzCard

    const bill = client.readFragment<FullBillFragment>({
        id: `Bill:${payment.accountId}`,
        fragment: FULL_BILL_FRAGMENT,
        fragmentName: 'FullBill',
    })
    const account = payment.account ?? null

    let accountData
    if (bill) {
        accountData = { img: bill?.institution, name: bill?.name, subline: bill.institution?.name }
    } else if (isPayOthers) {
        accountData = {
            img: { logoUrl: PayOthersImage },
            name: account?.name,
            subline: 'Pay Others',
        }
    } else if (isCard) {
        accountData = {
            img: { logoUrl: SpritzCardImage },
            name: account?.name,
            subline: 'Pre-funded card',
        }
    } else {
        accountData = { img: null, name: account?.name, subline: '' }
    }

    const latestTransaction = getLatestTransaction(payment)

    const chainInfo = latestTransaction?.network
        ? getChainInfo(latestTransaction.network as SupportedNetwork)
        : null

    const externalPayment = payment.externalPayment
    const isInProgress = [
        PaymentStatus.Scheduled,
        PaymentStatus.Pending,
        PaymentStatus.Sent,
    ].includes(payment.status)

    const paidAmount = payment.amount! + (payment.feeAmount ?? 0) - (payment.rewardsAmount ?? 0)

    const Wrapper: React.ReactNode = embedded ? EmbeddedWrapper : StyledCard

    return (
        <Wrapper>
            <Row align="space-between">
                <Row>
                    {accountData && (
                        <>
                            {accountData.img && (
                                <>
                                    <InstitutionImage institution={accountData.img} />
                                    <Spacer width={16} />
                                </>
                            )}
                            <TitleWrapper>
                                <Body.Med size={14} secondary>
                                    {accountData.subline}
                                </Body.Med>
                                <Body.Bold size={18}>{accountData.name}</Body.Bold>
                            </TitleWrapper>
                        </>
                    )}
                </Row>
                <StatusBadge status={payment.status} />
            </Row>
            <Spacer height={32} />
            <DataWrapper>
                <div
                    onClick={() => setExpandedDetail(!expandedDetail)}
                    style={{ cursor: 'pointer' }}
                >
                    {payment.amount && (
                        <Row align="space-between">
                            <Body.Med size={14}>Total payment amount:</Body.Med>
                            <Row>
                                <Body.SmBold size={14}>
                                    {formatCurrency(payment.amount)}
                                </Body.SmBold>
                                <ExpandDetailIcon size={24} active={expandedDetail} />
                            </Row>
                        </Row>
                    )}
                </div>
                <FadePresence renderKey={expandedDetail}>
                    {expandedDetail && (
                        <ExpandedAmountsWrapper>
                            {!!payment.rewardsAmount && (
                                <>
                                    <Row align="space-between">
                                        {payment?.directPayment?.testPayment && (
                                            <Body.Med size={14}>Test payment matched:</Body.Med>
                                        )}
                                        {!payment?.directPayment?.testPayment && (
                                            <Body.Med size={14}>Rewards applied:</Body.Med>
                                        )}
                                        <Body.SmBold size={14}>
                                            {formatCurrency(payment.rewardsAmount)}
                                        </Body.SmBold>
                                    </Row>
                                    <Spacer height={16} />
                                </>
                            )}
                            {!!payment.feeAmount && (
                                <>
                                    <Row align="space-between">
                                        <Body.Med size={14}>Spritz fee:</Body.Med>
                                        <Body.SmBold size={14}>
                                            {formatCurrency(payment.feeAmount)}
                                        </Body.SmBold>
                                    </Row>
                                    <Spacer height={16} />
                                </>
                            )}
                            <Row align="space-between">
                                <Body.SmBold size={14}>You paid:</Body.SmBold>
                                <Body.Bold size={14}>{formatCurrency(paidAmount)}</Body.Bold>
                            </Row>
                        </ExpandedAmountsWrapper>
                    )}
                </FadePresence>

                <Spacer height={16} />
                <Row align="space-between">
                    <Body.Med size={14}>Token used:</Body.Med>
                    <Body.SmBold size={14}>
                        {payment?.directPayment?.transactionReferences[0]?.sourceToken?.symbol}
                    </Body.SmBold>
                </Row>
                {chainInfo?.name && (
                    <>
                        <Spacer height={16} />
                        <Row align="space-between">
                            <Body.SmBold size={14}>Network:</Body.SmBold>
                            <Row>
                                <NetworkLogo
                                    network={
                                        CHAIN_ID_TO_NETWORK[chainInfo.chainId] as SupportedNetwork
                                    }
                                    size={14}
                                />
                                <Spacer width={4} />
                                <Body.Med size={14}>{chainInfo.name}</Body.Med>
                            </Row>
                        </Row>
                    </>
                )}
                {externalPayment?.createdAt && (
                    <>
                        <Spacer height={16} />
                        <Row align="space-between">
                            <Body.Med size={14}>Transaction date:</Body.Med>
                            <Body.SmBold size={14}>
                                {dayjs(externalPayment.createdAt).format('MMM DD, YYYY')}
                            </Body.SmBold>
                        </Row>
                    </>
                )}
                {externalPayment?.completionDate && (
                    <>
                        <Spacer height={16} />
                        <Row align="space-between">
                            <Body.Med size={14}>
                                {isInProgress ? 'Estimated delivery date:' : 'Delivery date:'}
                            </Body.Med>
                            <Body.SmBold size={14}>
                                {dayjs(externalPayment?.completionDate).format('MMM DD, YYYY')}
                            </Body.SmBold>
                        </Row>
                        {isBankTransfer && (
                            <>
                                <Spacer height={16} />
                                <InfoWrapper>
                                    <Column>
                                        <Body.Bold
                                            size={14}
                                            color={cssVar('--text-success', colors.seaSpray[75])}
                                        >
                                            Keep in mind
                                        </Body.Bold>
                                        <Body.Med
                                            size={14}
                                            color={cssVar('--text-success', colors.seaSpray[75])}
                                        >
                                            It can take up to 2 working days for your bank to credit
                                            the account.
                                        </Body.Med>
                                    </Column>
                                </InfoWrapper>
                            </>
                        )}
                    </>
                )}
            </DataWrapper>
            <Spacer height={16} />
            {latestTransaction && chainInfo && (
                <Row align="center">
                    <Button.Text
                        onClick={() =>
                            window.open(
                                chainInfo.getExplorerTransactionLink(latestTransaction.hash)
                            )
                        }
                    >
                        <Row>
                            <Body.Med size={12}>View transaction on the blockchain</Body.Med>
                            <Spacer width={8} />
                            <Icon icon={OpenNewSvg} />
                        </Row>
                    </Button.Text>
                </Row>
            )}
        </Wrapper>
    )
}
